import React, { useEffect, useState } from "react";
import { Box, Stack, Typography, Button } from "@mui/material";
import { LoadState } from "../../constants/enums";
import dummyImage from "../../assets/dummy_image.jpg";
import pdfImage from "../../assets/pdf.png";
import csvImage from "../../assets/icons/csv-file-icon.png";
import txtImage from "../../assets/icons/txt-file-icon.png";
import excelImage from "../../assets/icons/excel-file-icon.png";
import docImage from "../../assets/icons/doc-file-icon.png";
import pptImage from "../../assets/icons/ppt-file-icon.png";
import fileImage from "../../assets/icons/file-iicon.png";
import { ViewImageDialog } from "../FileUploadIcon/ViewImageDialog";
import { useFilePreview } from "../../hooks/useFilePreview";
import { IWhatsAppTemplateData } from "../../redux/whatsappTemplate/whatsappTemplate.types";
import { IQuickReplyTemplateData } from "../../redux/quickReplyTemplates/quickReplyTemplate.types";
import { ReadMoreText } from "../../views/marketingCampaign/components/ReadMoreText";
import { config } from "../../config";

export const getFileSrcUrl = (extensionType?: string) => {
  if (!extensionType) return fileImage;

  const ext = extensionType.toLowerCase();

  const fileIconMap: Record<string, string> = {
    pdf: pdfImage,

    csv: csvImage,

    xls: excelImage,
    xlsx: excelImage,
    sheet: excelImage,

    doc: docImage,
    docx: docImage,
    rtf: docImage,
    odt: docImage,

    txt: txtImage,

    ppt: pptImage,
    pptx: pptImage,
    odp: pptImage,
  };

  return fileIconMap[ext] || fileImage;
};

interface PreviewTemplateProps {
  template: IWhatsAppTemplateData | IQuickReplyTemplateData;
  loading?: LoadState;
  headerMedia?: File | null;
  headerType?: string;
  headerVariableValue?: string;
  bodyVariableValues?: { [key: string]: string };
  bodyVariableTypes?: { [key: string]: string };
  bgImage?: boolean;
  renderInsideReply?: boolean;
  isFromQuickReply?: boolean;
  isSender?: boolean; // New prop to determine if message is from sender
  isFromActivityRoot?: boolean;
  isFromTemplateCreation?: boolean;
}

export const PreviewTemplate: React.FC<PreviewTemplateProps> = ({
  template,
  loading = LoadState.Loaded,
  headerMedia,
  headerType,
  headerVariableValue,
  bodyVariableValues = {},
  bodyVariableTypes = {},
  bgImage = true,
  renderInsideReply = false,
  isFromQuickReply = false,
  isSender = false,
  isFromActivityRoot = false,
  isFromTemplateCreation = false,
}) => {
  console.log("PreviewTemplate ==> ", template)

  const { preview, previewFile, closePreview } = useFilePreview();

  const [imageSource, setImageSource] = useState<string | null>(null);
  const [processedBody, setProcessedBody] = useState("");
  const [orgImageUrl, setOrgImageUrl] = useState<string | null>(null);

  const handleViewDocumnetClick = (fileUrl: string) => {
    setOrgImageUrl(fileUrl);
    previewFile(fileUrl);
  };

  useEffect(() => {
    if (template.body?.text) {
      let bodyText = template.body.text;

      bodyText = bodyText.replace(/\{\{(\d+)\}\}/g, (match, index) => {
        const value = bodyVariableValues[index];
        if (value) {
          // If type is SELECT, wrap the value in curly braces
          if (bodyVariableTypes[index] === "SELECT") {
            return `{{${value}}}`;
          }
          return value;
        }
        return match;
      });

      Object.entries(bodyVariableValues).forEach(([key, value]) => {
        const regex = new RegExp(`\\{\\{${key}\\}\\}`, "g");
        // If type is SELECT, wrap the value in curly braces
        if (bodyVariableTypes[key] === "SELECT") {
          bodyText = bodyText.replace(regex, `{{${value}}}`);
        } else {
          bodyText = bodyText.replace(regex, value);
        }
      });

      bodyText = bodyText.replace(/\n/g, "<br />");

      setProcessedBody(bodyText);
    } else {
      // Clear processedBody when there's no body text
      setProcessedBody("");
    }
  }, [template, bodyVariableValues, bodyVariableTypes]);

  useEffect(() => {
    let source: string | null = null;

    // First priority: newly uploaded media (headerMedia File object)
    if (headerMedia instanceof File) {
      source = URL.createObjectURL(headerMedia);
    }
    // Second priority: header variable value (from form state)
    else if (headerVariableValue && !headerMedia) {
      source = headerVariableValue;
    }
    // Third priority: saved header_media data (from market campaign channel)
    else if (
      (template as any).header_media &&
      Array.isArray((template as any).header_media) &&
      (template as any).header_media.length > 0
    ) {
      source = (template as any).header_media[0].value;
    }
    // Fourth priority: saved attachment data (from market campaign channel or attachment object)
    else if (
      (template as any).attachment?.example &&
      (template as any).attachment.example !== ""
    ) {
      source = (template as any).attachment.example;
    }
    else if (
      (template as any).attachment &&
      typeof (template as any).attachment === "string" &&
      (template as any).attachment !== ""
    ) {
      source = (template as any).attachment;
    }
    // Fifth priority: template.attachments
    else if (
      template.attachment &&
      Array.isArray(template.attachment) &&
      template.attachment.length > 0 &&
      template.attachment[0]
    ) {
      source = template.attachment[0];
    }
    // Sixth priority: template.components with type HEADER
    else if (template.components && Array.isArray(template.components)) {
      const headerComponent = template.components.find(
        (comp: any) =>
          comp.type === "HEADER" &&
          (comp as any).example?.header_handle?.length > 0,
      );
      if ((headerComponent as any)?.example?.header_handle?.[0]) {
        source = (headerComponent as any).example.header_handle[0];
      }
    }
    // Seventh priority: template.header examples
    else if (template.header?.example?.header_handle?.[0]) {
      const headerHandle = template.header.example.header_handle[0];
      if (typeof headerHandle === "string") {
        source = headerHandle;
      } else if (headerHandle) {
        source = headerHandle.url || headerHandle.path || null;
      }
    }
    // Finally use placeholders
    else if (headerType === "IMAGE") {
      source = dummyImage;
    } else if (headerType === "DOCUMENT") {
      source = "/images/document-placeholder.png";
    }

    setImageSource(source);

    return () => {
      if (headerMedia instanceof File && source) {
        URL.revokeObjectURL(source);
      }
    };
  }, [headerType, headerMedia, headerVariableValue, template]);

  const renderHeaderContent = () => {
    // Check for media URL in multiple places
    let attachmentUrl: string | null = null;

    // First priority: newly uploaded media (headerMedia File object)
    if (headerMedia instanceof File) {
      attachmentUrl = URL.createObjectURL(headerMedia);
    }
    // Second priority: header variable value (from form state)
    else if (headerVariableValue && !headerMedia) {
      attachmentUrl = headerVariableValue;
    }
    // Third priority: saved header_media data (from market campaign channel)
    else if ((template as any).header_media && Array.isArray((template as any).header_media) &&
      (template as any).header_media.length > 0
    ) {
      attachmentUrl = (template as any).header_media[0].value;
    }
    // Fourth priority: saved attachment data (from market campaign channel or attachment object)
    else if (
      (template as any).attachment?.example &&
      (template as any).attachment.example !== ""
    ) {
      attachmentUrl = (template as any).attachment.example;
    }
    else if (
      (template as any).attachment &&
      typeof (template as any).attachment === "string" &&
      (template as any).attachment !== ""
    ) {
      attachmentUrl = (template as any).attachment;
    }
    // Fifth priority: template.attachments
    else if (
      template.attachment &&
      Array.isArray(template.attachment) &&
      template.attachment?.length > 0 &&
      template.attachment[0] !== ""
    ) {
      attachmentUrl = template.attachment[0];
    }
    // Sixth priority: template.components with type HEADER
    else if (template.components && Array.isArray(template.components)) {
      const headerComponent = template.components.find(
        (comp: any) => comp.type === "HEADER" && (comp as any).example?.header_handle?.length > 0,
      );
      if ((headerComponent as any)?.example?.header_handle?.[0]) {
        attachmentUrl = (headerComponent as any).example.header_handle[0];
      }
    }
    // Seventh priority: template.header.example.header_handle
    else if (template.header?.example?.header_handle?.[0]) {
      const headerHandle = template.header.example.header_handle[0];
      if (typeof headerHandle === "string") {
        attachmentUrl = headerHandle;
      } else if (headerHandle) {
        attachmentUrl = (headerHandle as any).url || (headerHandle as any).path || null;
      }
    }

    // Ensure attachmentUrl is a string before processing
    if (attachmentUrl && typeof attachmentUrl !== "string") {
      attachmentUrl = String(attachmentUrl); // Convert to string or handle appropriately
    }

    if (attachmentUrl) {
      const s3BaseUrl = config.assets.s3BaseUrl

      if (!attachmentUrl?.startsWith(s3BaseUrl) && !attachmentUrl?.startsWith("http")) {
        attachmentUrl = `${s3BaseUrl}/${attachmentUrl}`;
      }

      // Get file extension to determine if it's an image, video, or document
      const fileName = attachmentUrl.split("/").pop() || "";
      const fileExt = fileName.split(".").pop()?.toLowerCase() || "";
      const isImageFile = [
        "jpg",
        "jpeg",
        "png",
        "gif",
        "bmp",
        "webp",
        "svg",
        "ico",
        "tiff",
        "tif",
      ].includes(fileExt);
      const isVideoFile = [
        "mp4",
        "avi",
        "mov",
        "wmv",
        "flv",
        "mkv",
        "webm",
      ].includes(fileExt);
      const isDocumentFile = [
        "pdf",
        "doc",
        "docx",
        "xls",
        "xlsx",
        "txt",
        "ppt",
        "pptx",
        "rtf",
        "odt",
        "ods",
        "odp",
        "sheet",
        "csv",
      ].includes(fileExt);

      // Check if we have content to render (either from template.content or template.body.text or template.text)
      const hasContent =
        (template as IWhatsAppTemplateData).content ||
        (template as IWhatsAppTemplateData).body?.text ||
        (template as any).text;
      if (hasContent) {
        // Render based on file type
        if (isImageFile) {
          return (
            <img
              src={attachmentUrl}
              alt="Attachment"
              style={{
                width: "100%",
                borderRadius: 2,
                objectFit: "cover",
                maxHeight: 300,
                cursor: "pointer",
              }}
              onError={(e) => {
                (e.target as HTMLImageElement).src = dummyImage;
              }}
              onClick={() =>
                attachmentUrl && handleViewDocumnetClick(attachmentUrl)
              }
            />
          );
        } else if (isVideoFile) {
          return (
            <Stack spacing={1} direction={"column"}>
              <video
                controls
                style={{
                  width: "100%",
                  borderRadius: 2,
                  maxHeight: 300,
                }}
              >
                <source src={attachmentUrl} type={`video/${fileExt}`} />
                Your browser does not support the video tag.
              </video>
            </Stack>
          );
        } else if (isDocumentFile || headerType === "DOCUMENT") {
          const fileBaseName =
            (template as any).attachment?.filename ||
            fileName.replace(/\.[^/.]+$/, ""); // Remove extension

          // Determine icon and file type text based on extension
          let fileTypeText = "DOCUMENT";

          if (fileExt === "pdf" || (!fileExt && headerType === "DOCUMENT")) {
            fileTypeText = "PDF";
          } else if (fileExt === "doc" || fileExt === "docx") {
            fileTypeText = "DOC";
          } else if (
            fileExt === "xls" ||
            fileExt === "xlsx" ||
            fileExt === "sheet"
          ) {
            fileTypeText = "XLS";
          } else if (fileExt === "txt") {
            fileTypeText = "TXT";
          } else if (fileExt === "csv") {
            fileTypeText = "CSV";
          }

          // Mock file size - in a real app you'd get this from the file metadata
          const fileSize = Math.floor(Math.random() * 900) + 100; // Random size between 100-1000 KB
          const sizeText =
            fileSize < 1024
              ? `${fileSize} KB`
              : `${(fileSize / 1024).toFixed(1)} MB`;

          return (
            <Box
              onClick={() =>
                attachmentUrl && window.open(attachmentUrl, "_blank")
              }
              sx={{
                display: "flex",
                maxWidth: "300px",
                width: "100%",
                alignItems: "end",
                backgroundColor: "#072745", //hirgb(0, 45, 61)
                borderRadius: "7.5px",
                minHeight: "100px",
                p: "10px",
                cursor: "pointer",
                overflow: "hidden",
                "&:hover": {
                  // backgroundColor: "#e5e5e7",
                },
              }}
            >
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                sx={{ width: "100%", minWidth: 0 }}
              >
                {/* <FileIcon sx={{ color: "#b90e13" }} /> */}
                <img
                  src={getFileSrcUrl(fileExt)}
                  alt={fileExt}
                  width={"26px"}
                  height={"26px"}
                  style={{ cursor: "pointer", flexShrink: 0 }}
                  onClick={() => pdfImage && handleViewDocumnetClick(pdfImage)}
                />

                <Stack sx={{ overflow: "hidden", flex: 1, minWidth: 0 }}>
                  <Typography
                    variant="body1"
                    sx={{
                      fontWeight: 500,
                      color: "white",
                      width: "100%",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {fileBaseName}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      color: "#bcc2c5ff",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {fileTypeText} • {sizeText}
                  </Typography>
                </Stack>
              </Stack>
            </Box>
          );
        }
      }

      if (headerType === "DOCUMENT" && !headerMedia) {
        const fileName = attachmentUrl.split("/").pop() || "document";
        const fileExt = fileName.split(".").pop()?.toLowerCase() || "";
        const fileBaseName = fileName.replace(/\.[^/.]+$/, ""); // Remove extension

        // Determine icon and file type text based on extension
        let fileTypeText = "DOCUMENT";

        if (fileExt === "pdf") {
          fileTypeText = "PDF";
        } else if (fileExt === "doc" || fileExt === "docx") {
          fileTypeText = "DOC";
        } else if (fileExt === "xls" || fileExt === "xlsx") {
          fileTypeText = "XLS";
        }

        // Mock file size - in a real app you'd get this from the file metadata
        const fileSize = Math.floor(Math.random() * 900) + 100; // Random size between 100-1000 KB
        const sizeText =
          fileSize < 1024
            ? `${fileSize} KB`
            : `${(fileSize / 1024).toFixed(1)} MB`;

        return (
          <Box
            onClick={() =>
              attachmentUrl && window.open(attachmentUrl, "_blank")
            }
            sx={{
              display: "flex",
              maxWidth: "300px",
              width: "100%",
              alignItems: "end",

              backgroundColor: "#072745",
              borderRadius: "7.5px",
              minHeight: "100px",
              p: "10px",

              cursor: "pointer",
              overflow: "hidden",
              "&:hover": {
                // backgroundColor: "#e5e5e7",
              },
            }}
          >
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              sx={{ width: "100%", minWidth: 0 }}
            >
              {/* <FileIcon sx={{ color: "#b90e13" }} /> */}
              <img
                src={getFileSrcUrl(fileExt)}
                alt={fileExt}
                width={"26px"}
                height={"26px"}
                style={{ cursor: "pointer", flexShrink: 0 }}
                onClick={() => pdfImage && handleViewDocumnetClick(pdfImage)}
              />

              <Stack sx={{ overflow: "hidden", flex: 1, minWidth: 0 }}>
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 500,

                    color: "white",
                    width: "100%",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {fileBaseName}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: "#bcc2c5ff",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {fileTypeText} • {sizeText}
                </Typography>
              </Stack>
            </Stack>
          </Box>
        );
      } else if (headerType === "IMAGE" && !headerMedia) {
        return (
          <img
            src={attachmentUrl}
            alt="Attachment"
            style={{
              width: "100%",

              borderRadius: 2,
              objectFit: "cover",
              maxHeight: 300,
              cursor: "pointer",
            }}
            onError={(e) => {
              (e.target as HTMLImageElement).src = dummyImage;
            }}
            onClick={() =>
              attachmentUrl && handleViewDocumnetClick(attachmentUrl)
            }
          />
        );
      } else if (headerType === "IMAGE" && headerMedia) {
        return (
          <img
            // src={attachmentUrl}
            src={headerMedia ? URL.createObjectURL(headerMedia) : attachmentUrl}
            alt="Attachment"
            style={{
              width: "100%",

              borderRadius: 2,
              objectFit: "cover",
              maxHeight: 300,
              cursor: "pointer",
            }}
            onError={(e) => {
              (e.target as HTMLImageElement).src = dummyImage;
            }}
            onClick={() =>
              headerMedia
                ? headerMedia &&
                handleViewDocumnetClick(URL.createObjectURL(headerMedia))
                : attachmentUrl && handleViewDocumnetClick(attachmentUrl)
            }
          />
        );
      } else if (headerType === "VIDEO" && !headerMedia) {
        return (
          <Stack spacing={1} direction={"column"}>
            <video
              controls
              style={{
                width: "100%",
                borderRadius: 2,
                maxHeight: 300,
              }}
            >
              <source src={attachmentUrl} type="video/mp4" />
              Your browser does not support the video tag.
            </video>
          </Stack>
        );
      } else if (headerType === "VIDEO" && headerMedia) {
        return (
          <Stack spacing={1} direction={"column"}>
            <video
              controls
              style={{
                width: "100%",
                borderRadius: 2,
                maxHeight: 300,
              }}
            >
              <source
                src={
                  headerMedia ? URL.createObjectURL(headerMedia) : attachmentUrl
                }
                type="video/mp4"
              />
              Your browser does not support the video tag.
            </video>
          </Stack>
        );
      }
    }

    if (!imageSource) return null;

    if (headerType === "IMAGE") {
      return (
        <Stack spacing={1} direction={"column"}>
          <img
            src={imageSource}
            alt={template.header?.text || "Header image"}
            style={{
              width: "100%",
              borderRadius: 2,
              objectFit: "cover",
              maxHeight: 300,
              cursor: "pointer",
            }}
            onError={(e) => {
              (e.target as HTMLImageElement).src = dummyImage;
            }}
            onClick={() => imageSource && handleViewDocumnetClick(imageSource)}
          />
        </Stack>
      );
    } else if (headerType === "VIDEO") {
      return (
        <Stack spacing={1} direction={"column"}>
          <video
            controls
            style={{
              width: "100%",
              borderRadius: 2,
              maxHeight: 300,
            }}
          >
            <source src={imageSource} type="video/mp4" />
            Your browser does not support the video tag.
          </video>
        </Stack>
      );
    } else if (headerType === "DOCUMENT") {
      return (
        <iframe
          src={imageSource}
          style={{ width: "100%", height: "100%", border: "none" }}
          title="Document"
          onClick={() => {
            // Handle document download/view
            if (imageSource) {
              window.open(imageSource, "_blank");
            }
          }}
        />
      );
    }
    return null;
  };

  const isOTPButton = (
    button: any,
  ): button is { type: "OTP"; text?: string; value?: { text?: string } } => {
    return button.type === "OTP" && (!!button.text || !!button.value?.text);
  };

  const isCategoryAuthentication = (template as any).category === "AUTHENTICATION";

  const bodyComponent = (template as any).components?.find((c: any) => c.type === "BODY");
  const bodyObj = (template as any).body;
  const addSecurityRecommendation =
    bodyObj?.value?.add_security_recommendation ??
    bodyComponent?.add_security_recommendation ??
    false;

  const footerComponent = (template as any).components?.find((c: any) => c.type === "FOOTER");
  const footerObj = (template as any).footer;
  const codeExpirationMinutes =
    footerObj?.value?.code_expiration_minutes ??
    footerComponent?.code_expiration_minutes ??
    null;

  const authBodyText = `123456 is your verification code.${addSecurityRecommendation ? " For your security, do not share this code." : ""
    }`;

  const authFooterText = codeExpirationMinutes
    ? `Expires in ${codeExpirationMinutes} minutes`
    : footerObj?.text || "Expires in 10 minutes";

  return (
    <>
      <Box
        borderRadius={2}
        // maxWidth={headerType === "DOCUMENT" ? "300px" : "100%"}

        sx={{
          width: "100%",

          overflow: "visible",
          display: "flex",
          flexDirection: "column",
          backgroundColor: "transparent",
        }}
      >
        <Stack
          sx={{
            backgroundColor: renderInsideReply
              ? "none"
              : isSender
                ? "#d9fdd3"
                : "#ffffff",
            borderRadius: isSender
              ? "7.5px 0px 7.5px 7.5px"
              : "0px 7.5px 7.5px 7.5px",
            color: isSender ? "#111b21" : "#111b21",
            py: renderInsideReply ? 0 : 1.5,
            px: renderInsideReply ? 0 : 1.5,
            boxShadow: "0 1px 0.5px rgba(0, 0, 0, 0.13)",
            position: "relative",
            // maxWidth: { xs: "95%", sm: "85%" },
            maxWidth: { xs: "95%", sm: isFromActivityRoot ? "95%" : "85%" },
            width: "fit-content",
            overflow: "visible",
            wordBreak: "break-word",
            marginLeft: isSender ? "auto" : "0",
            marginRight: isSender ? "0" : "auto",
          }}
        >
          {template.header?.text && (
            <Stack spacing={1} mb={1} direction={"column"}>
              <Typography variant="body1" fontSize={"1rem"} fontWeight={600}>
                {template.header.text}
              </Typography>
            </Stack>
          )}

          {renderHeaderContent()}

          <Stack spacing={1} direction={"column"}>
            {isFromQuickReply ? (
              <ReadMoreText
                text={(template as IQuickReplyTemplateData).text}
                maxLines={10}
                variant="body2"
              />
            ) : isCategoryAuthentication ? (
              <ReadMoreText
                isFromTemplateCreation={isFromTemplateCreation}
                text={authBodyText}
                maxLines={10}
                variant="body2"
              />
            ) : (
              <ReadMoreText
                isFromTemplateCreation={isFromTemplateCreation}
                text={processedBody || template.body.text}
                maxLines={10}
                variant="body2"
              />
            )}
          </Stack>

          {(isCategoryAuthentication ? authFooterText : template.footer?.text) && (
            <Stack spacing={1} mt={2} direction={"column"}>
              <Typography variant="body1">
                {isCategoryAuthentication ? authFooterText : template.footer.text}
              </Typography>
            </Stack>
          )}

          {template.buttons?.buttons &&
            template.buttons?.buttons?.length > 0 && (
              <Stack direction="column" mt={1} spacing={1}>
                {template.buttons?.buttons
                  ?.filter((x) => {
                    if (isOTPButton(x)) {
                      return true;
                    }
                    return !!x.text;
                  })
                  .map((button, index) => {
                    let buttonText = "";

                    if (isOTPButton(button)) {
                      buttonText = button.text || button.value?.text || "Copy Code";
                    } else if (button.text) {
                      buttonText = button.text;
                    }

                    return (
                      <Button
                        key={index}
                        sx={{
                          width: "100%",
                          backgroundColor: "#808080",
                          "&:hover": { backgroundColor: "#808080" },
                          color: "#fff",
                        }}
                      >
                        {buttonText}
                      </Button>
                    );
                  })}
              </Stack>
            )}
        </Stack>
      </Box>
      {preview.open && (
        <ViewImageDialog
          open={preview.open}
          file={preview.file}
          fileType={preview.type}
          onClose={closePreview}
          orgImageUrl={orgImageUrl || ""}
        />
      )}
    </>
  );
};
